import { Component, signal, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MsfInput } from '@components/ui/forms/input/input';
import { CheckboxComponent } from '@components/checkbox';
import { ButtonDirective } from 'primeng/button';
import { SaveButton } from '@components/button/save-button/save-button/save-button';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TextareaComponent } from '@components/textarea';
import { Divider } from 'primeng/divider';
import { SystemDocumentation } from '@6grain/masofa-monolith-api-client/common';
import { SystemDocumentationService } from '@6grain/masofa-monolith-api-client/common/api/systemDocumentation.service';
import { LocalizationString } from '@6grain/masofa-monolith-api-client/common';
import {Toast} from 'primeng/toast';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import { DocumentFileUploadComponent, FileInfo } from '@components/document-file-upload/document-file-upload.component';
import { CommonModule } from '@angular/common';
import { MsfSelect } from '@components/select/select';
import { HttpClient } from '@angular/common/http';
import { environment } from '@root/src/environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';

interface LocalizationFileStorageItem {
  'uz-Latn-UZ'?: string | null;
  'en-US'?: string | null;
  'ru-RU'?: string | null;
  valuesJson?: string | null;
  [key: string]: string | null | undefined;
}

interface SystemDocumentationFormData extends Omit<SystemDocumentation, 'fileStorageId'> {
  names: LocalizationString;
  isActive?: boolean;
  isPublished?: boolean;
  fileStorageIds?: LocalizationFileStorageItem;
  blockId?: string;
}

interface SystemDocumentationBlockOption {
  id: string;
  name: string;
  names?: LocalizationString;
}

@Component({
  selector: 'app-add-system-documentation-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    MsfInput,
    CheckboxComponent,
    TextareaComponent,
    Divider,
    Toast,
    PrimaryButton,
    SecondaryButton,
    DocumentFileUploadComponent,
    MsfSelect
  ],
  providers: [MessageService, DialogService],
  templateUrl: './add-system-documentation-form.html',
  styleUrl: './add-system-documentation-form.scss'
})
export class AddSystemDocumentationForm implements OnInit {
  @ViewChild('fileUploadUz') fileUploadComponentUz?: DocumentFileUploadComponent;
  @ViewChild('fileUploadEn') fileUploadComponentEn?: DocumentFileUploadComponent;
  @ViewChild('fileUploadRu') fileUploadComponentRu?: DocumentFileUploadComponent;

  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  isSaving = signal(false);
  isCanceling = signal(false);

  formData: SystemDocumentationFormData = {
    status: 0,
    isActive: true,
    isPublished: false,
    names: {
      'ru-RU': '',
      'en-US': '',
      'uz-Latn-UZ': ''
    },
    fileStorageIds: {
      'uz-Latn-UZ': null,
      'en-US': null,
      'ru-RU': null
    },
    blockId: undefined,
    comment: '',
    orderCode: '',
    createAt: new Date().toISOString().split('T')[0]
  };

  readonly supportedLanguages = environment.langs.map(lang => lang.value) || ['uz-Latn-UZ', 'en-US', 'ru-RU'];

  mode: 'create' | 'edit' = 'create';

  blockOptions: SystemDocumentationBlockOption[] = [];

  // Track field errors
  fieldErrors = {
    names: false,
    visible: false
  };

  constructor(
    public tr: TranslateService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private systemDocumentationService: SystemDocumentationService,
    private messageService: MessageService
  ) {
  }

  ngOnInit() {
    if (this.config.data) {
      this.mode = this.config.data.mode || 'create';

      if (this.mode === 'edit' && this.config.data.model) {
        const model = this.config.data.model;
        this.formData = {
          ...this.formData,
          ...model
        };

        // Ensure fileStorageIds is properly initialized
        if (model.fileStorageIds) {
          const fileStorageIds = model.fileStorageIds as { [key: string]: string | null | undefined };
          this.formData.fileStorageIds = {
            'uz-Latn-UZ': fileStorageIds['uz-Latn-UZ'] || null,
            'en-US': fileStorageIds['en-US'] || null,
            'ru-RU': fileStorageIds['ru-RU'] || null
          };
        } else {
          // Initialize empty if not present
          this.formData.fileStorageIds = {
            'uz-Latn-UZ': null,
            'en-US': null,
            'ru-RU': null
          };
        }
      }
    }

    this.loadBlocks();
  }

  loadBlocks(): void {
    const apiUrl = `${environment.api}/dictionaries/SystemDocumentationBlock/GetByQuery`;

    this.http.post<any[]>(apiUrl, {
      offset: 0,
      take: 9999,
      filters: []
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.blockOptions = (response || []).map(block => ({
            id: block.id,
            name: this.getNameForLanguage(block),
            names: block.names
          }));
        },
        error: (error) => {
          console.error('Error loading blocks:', error);
          this.blockOptions = [];
        }
      });
  }

  getNameForLanguage(block: any): string {
    if (!block?.names) return '';

    const langMap: Record<string, string> = {
      'en': 'en-US',
      'en-us': 'en-US',
      'ru': 'ru-RU',
      'ru-ru': 'ru-RU',
      'uz': 'uz-Latn-UZ',
      'uz-latn': 'uz-Latn-UZ'
    };

    const currentLang = this.tr.currentLang;
    const langCode = langMap[currentLang?.toLowerCase()] || '';

    return block.names[langCode] || block.names['en-US'] || block.names['ru-RU'] || block.names['uz-Latn-UZ'] || '';
  }

  async saveSystemDocumentation(): Promise<void> {
    this.validateFields();

    if (!this.isFormValid()) {
      const missingFields: string[] = [];

      if (this.fieldErrors.names) {
        missingFields.push(this.tr.instant('Document Name'));
      }

      const detail = this.tr.instant('Please fill the following required fields') + ': ' + missingFields.join(', ');

      this.messageService.add({
        severity: 'error',
        summary: this.tr.instant('Validation Error'),
        detail: detail,
        life: 5000
      });
      return;
    }

    this.isSaving.set(true);

    try {
      // Prepare form data without files first
      const modelToSave = { ...this.formData };

      // Send initial request without file (or with first file if exists)
      const filesToUpload: Array<{ file: File; language: string }> = [];

      const uzFile = this.fileUploadComponentUz?.getLocalFile();
      const enFile = this.fileUploadComponentEn?.getLocalFile();
      const ruFile = this.fileUploadComponentRu?.getLocalFile();

      if (uzFile) filesToUpload.push({ file: uzFile, language: 'uz-Latn-UZ' });
      if (enFile) filesToUpload.push({ file: enFile, language: 'en-US' });
      if (ruFile) filesToUpload.push({ file: ruFile, language: 'ru-RU' });

      // First request - create/update document with first file (if any)
      const firstFile = filesToUpload.length > 0 ? filesToUpload[0] : null;
      const modelJson = JSON.stringify(modelToSave);

      // Use HttpClient to send multipart form data with language parameter
      const formData = new FormData();
      if (firstFile) {
        formData.append('file', firstFile.file);
        formData.append('language', firstFile.language);
      }
      formData.append('model', modelJson);

      const apiUrl = `${environment.api}/common/SystemDocumentation/CreateWithFile`;
      const response = await this.http.post<string>(apiUrl, formData).toPromise();

      if (!response) {
        throw new Error('Failed to save document');
      }

      const documentId = response;

      // Upload remaining files sequentially
      for (let i = 1; i < filesToUpload.length; i++) {
        const fileInfo = filesToUpload[i];
        const updateFormData = new FormData();
        updateFormData.append('file', fileInfo.file);
        updateFormData.append('language', fileInfo.language);

        // Update model with document ID for subsequent uploads
        const updateModel = { ...modelToSave, id: documentId };
        updateFormData.append('model', JSON.stringify(updateModel));

        await this.http.post<string>(apiUrl, updateFormData).toPromise();
      }

      this.isSaving.set(false);
      const msg = this.mode === 'edit' ? 'System documentation updated successfully' : 'System documentation created successfully';
      this.showSuccessMessage(msg);
      this.ref.close({ success: true, data: documentId });
    } catch (error: any) {
      this.handleError(error);
    }
  }

  cancelForm(): void {
    this.isCanceling.set(true);
    this.ref.close({ success: false });
  }

  isFormValid(): boolean {
    return !!(
      this.formData.names['uz-Latn-UZ']?.trim() ||
      this.formData.names['en-US']?.trim() ||
      this.formData.names['ru-RU']?.trim()
    );
  }

  validateFields(): void {
    this.fieldErrors.names = !this.isFormValid();
    this.fieldErrors.visible = this.formData.visible === undefined || this.formData.visible === null;
  }

  onNameChange(): void {
    if (this.fieldErrors.names) {
      this.fieldErrors.names = !this.isFormValid();
    }
  }

  onFileSelected(language: string, fileInfo: FileInfo): void {
    // New file selected for specific language
    if (!this.formData.fileStorageIds) {
      this.formData.fileStorageIds = {
        'uz-Latn-UZ': null,
        'en-US': null,
        'ru-RU': null
      };
    }
    // File will be uploaded on save, so we don't set fileStorageId here
  }

  onFileRemoved(language: string): void {
    // File removed for specific language
    if (this.formData.fileStorageIds) {
      this.formData.fileStorageIds[language as keyof LocalizationFileStorageItem] = null;
    }
  }

  getFileStorageIdForLanguage(language: string): string | undefined {
    return this.formData.fileStorageIds?.[language as keyof LocalizationFileStorageItem] || undefined;
  }

  onVisibleChange(): void {
    if (this.fieldErrors.visible) {
      this.fieldErrors.visible = this.formData.visible === undefined || this.formData.visible === null;
    }
  }

  private showSuccessMessage(detail: string): void {
    this.messageService.add({
      severity: 'success',
      summary: this.tr.instant('Success'),
      detail: this.tr.instant(detail),
      life: 3000
    });
  }

  private handleError(error: any): void {
    this.isSaving.set(false);
    const errorMessage = error?.error?.message || error?.message || 'An error occurred';
    this.messageService.add({
      severity: 'error',
      summary: this.tr.instant('Error'),
      detail: this.tr.instant(errorMessage),
      life: 5000
    });
  }
}

